import { Box, Divider, Stack, Typography } from '@mui/material';
import { useAppSelector } from 'core/store/hooks';
import { DARKER_GREY, LIGHT_BLACK } from 'utils/theme';
import { useDispatch } from 'react-redux';
import { setSearchValue, updateClaim } from 'core/store/claimsV2Slice';
import { useCheckClaim } from 'pages/PlaygroundV2Page/hooks/useCheckClaim';
import { useGenerate } from 'pages/PlaygroundV2Page/hooks/useGenerate';
import ArrowUpCircleIcon from 'assets/img/icons/ArrowUpCircleIcon';

interface RephrasedClaimsProps {
  claim: Claim;
}

const RephrasedClaims = ({ claim }: RephrasedClaimsProps) => {
  const generatedClaims = useAppSelector(
    (state) => state.claimsV2.generatedClaimsForClaim[claim.claim] || []
  );
  const searchValue = useAppSelector((state) => state.claimsV2.searchValue);
  const dispatch = useDispatch();
  const checkClaim = useCheckClaim();
  const getGeneratedClaims = useGenerate();

  const handleClaimClick = (rephrasedText: string) => {
    const updatedClaim: Claim & { oldClaimText?: string } = {
      ...claim,
      claim: rephrasedText,
      oldClaimText: claim.claim,
    };

    dispatch(updateClaim(updatedClaim));
    checkClaim(rephrasedText);
    getGeneratedClaims(rephrasedText);

    const updatedSearchValue = searchValue.replace(claim.claim, rephrasedText);
    dispatch(setSearchValue(updatedSearchValue));
  };

  return (
    <Box
      sx={{
        padding: '24px 144px 0 30px',
        borderBottomLeftRadius: '12px',
        borderBottomRightRadius: '12px',
        backgroundColor: 'white',
      }}
    >
      <Typography fontSize="18px" mb={2} fontWeight="400">
        The available evidence for this claim is limited, conflicting, or of low quality. More
        research is needed. Try to rephrase the claim on full text or select one of the suggested
        claims:
      </Typography>
      {generatedClaims?.length > 0 ? (
        generatedClaims.map((text, index) => (
          <Box key={index}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              py={1}
              mt={2}
              mb={2}
              onClick={() => handleClaimClick(text)}
              sx={{
                cursor: 'pointer',
                '&:hover .claim-text': {
                  color: LIGHT_BLACK,
                  fontWeight: '500',
                },
              }}
            >
              <Typography
                className="claim-text"
                fontSize="18px"
                sx={{
                  color: DARKER_GREY,
                  mr: 2,
                }}
              >
                {index + 1}. {text}
              </Typography>
              <ArrowUpCircleIcon rotation={90} />
            </Stack>
            {index < generatedClaims.length - 1 && <Divider />}
          </Box>
        ))
      ) : (
        <Typography fontSize="18px" color="gray">
          No rephrasings available.
        </Typography>
      )}
    </Box>
  );
};

export default RephrasedClaims;
